import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { VA_FORM_IDS } from 'platform/forms/constants';
import SaveInProgressIntro from 'platform/forms/save-in-progress/SaveInProgressIntro';
import { WIZARD_STATUS_COMPLETE } from 'platform/site-wide/wizard';

import { isExpired } from '../utils/dates';
import { getStartUrl, getWizardStatus } from '../utils/entryFlow';
import { trackScreenerSkipForInProgressForm } from '../../all-claims/utils/tracking/features/screenerTracking';
import { resetScreenerSession } from '../../all-claims/subtask/screenerSession';

const IntroStartLinks = ({ sipProps, loggedIn }) => {
  const savedForms = useSelector(
    state => state.user?.profile?.savedForms || [],
  );
  const startUrl = useSelector(getStartUrl);
  // The active version supplies its own formId via sipProps so the in-progress
  // form check matches the right saved form (v1: 21-526EZ, v2: 21-526EZ-V2).
  const formId = sipProps?.formId || VA_FORM_IDS.FORM_21_526EZ;
  const formVersion = formId === VA_FORM_IDS.FORM_21_526EZ_V2 ? 'v2' : 'v1';
  const hasInProgressForm = savedForms.some(
    savedForm => savedForm.form === formId && !isExpired(savedForm.metadata),
  );
  // Snapshot at mount: nothing writes the wizard status while this page is open.
  const [screenerStatus] = useState(getWizardStatus);
  const screenerComplete = screenerStatus === WIZARD_STATUS_COMPLETE;

  // Veterans with an in-progress form and no screener status are exactly the
  // cohort the legacy auto-redirect wrongly bounced to /get-started.
  const hasTrackedRef = useRef(false);
  useEffect(() => {
    if (
      loggedIn &&
      hasInProgressForm &&
      screenerStatus === null &&
      !hasTrackedRef.current
    ) {
      trackScreenerSkipForInProgressForm({ formVersion });
      hasTrackedRef.current = true;
    }
  }, [loggedIn, hasInProgressForm, screenerStatus, formVersion]);

  if (loggedIn && !hasInProgressForm && !screenerComplete) {
    return (
      <>
        <va-alert
          status="info"
          class="vads-u-margin-top--2 vads-u-margin-bottom--4"
        >
          <h3 slot="headline">We’ve prefilled some of your information</h3>
          <p className="vads-u-margin-top--0">
            Since you’re signed in, we’ve prefilled part of your application
            based on your profile details. You can also save your application in
            progress and come back later to finish filling it out.
          </p>
        </va-alert>
        <va-link-action
          class="vads-u-margin-bottom--4"
          href={startUrl}
          onClick={() => resetScreenerSession()}
          text="Start your disability compensation application"
          type="primary-entry"
        />
      </>
    );
  }

  return <SaveInProgressIntro {...sipProps} />;
};

IntroStartLinks.propTypes = {
  loggedIn: PropTypes.bool,
  sipProps: PropTypes.object,
};

export default IntroStartLinks;
